import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

interface NavbarProps {
  isScrolled: boolean;
}

export default function Navbar({ isScrolled }: NavbarProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      if (isDropdownOpen) setIsDropdownOpen(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDropdownOpen]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  const textColor = isHome && !isScrolled
    ? 'text-background-50'
    : 'text-foreground-950';
  const subtleColor = isHome && !isScrolled
    ? 'text-background-50/80 hover:text-background-50'
    : 'text-foreground-700 hover:text-foreground-950';
  const borderColor = isHome && !isScrolled
    ? 'border-background-50/20'
    : 'border-background-200/70';
  const dropdownBg = isHome && !isScrolled
    ? 'bg-background-50'
    : 'bg-background-50';
  const ctaStyle = isHome && !isScrolled
    ? 'bg-background-50 text-foreground-950 hover:bg-background-50/90'
    : 'bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600';
  const ctaOutline = isHome && !isScrolled
    ? 'border border-background-50/40 text-background-50 hover:bg-background-50/15'
    : 'border border-primary-500 text-primary-500 hover:bg-primary-50';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
            <span className={`font-heading text-xl md:text-2xl font-bold tracking-tight transition-colors duration-300 ${textColor}`}>
              AllChinaBuy<span className="text-primary-500">.works</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            <Link
              to="/"
              className={`text-sm font-semibold transition-colors whitespace-nowrap ${subtleColor}`}
            >
              Home
            </Link>

            <Link
              to="/article/allchinabuyspreadsheet-complete-guide-2026"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${subtleColor}`}
            >
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${subtleColor}`}
            >
              AllChinaBuy Sheet
            </a>

            <div
              className="relative"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <button
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap ${subtleColor}`}
              >
                Categories
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                </span>
              </button>

              {isDropdownOpen && (
                <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 ${dropdownBg} border ${borderColor} rounded-xl py-3 px-2 min-w-[300px] shadow-lg z-50`}>
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-primary-500 text-xs`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${ctaOutline}`}
            >
              AllChinaBuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${ctaStyle}`}
            >
              Buy It Now
            </a>
          </div>

          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center flex-shrink-0"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl transition-colors duration-300 ${textColor} ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-4-line text-primary-500"></i>
              </span>
              Home
            </Link>

            <Link
              to="/article/allchinabuyspreadsheet-complete-guide-2026"
              className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line text-primary-500"></i>
              </span>
              Shopping Guide
            </Link>

            <div className="border-t border-background-200/70 my-2"></div>

            <p className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Categories</p>

            {categories.map((cat) => (
              <a
                key={cat.slug}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <span className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100/80 flex-shrink-0">
                  <i className={`${cat.icon} text-accent-600 text-sm`}></i>
                </span>
                {cat.name}
              </a>
            ))}

            <div className="border-t border-background-200/70 my-2"></div>

            <div className="flex flex-col gap-2 px-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-3 border border-primary-500 text-primary-500 rounded-full text-sm font-semibold hover:bg-primary-50 transition-colors text-center whitespace-nowrap"
              >
                AllChinaBuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors text-center whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}